import { applyCommand, createCommandId } from '../commands'
import { BudgetEventBus, budgetEventBus } from '../events'
import { BudgetHistory } from '../history'
import { budgetSerializer, BudgetSerializer } from '../serializer'
import { budgetValidator, BudgetValidator } from '../validators'
import { budgetCalculator, BudgetCalculator } from './BudgetCalculator'
import type {
  BudgetCommand,
  BudgetCommandResult,
  BudgetDocument,
  BudgetEngineOptions,
  BudgetEvent,
  BudgetEventType,
  BudgetListener,
  BudgetSnapshot,
  BudgetValidationResult,
} from '../types'
import { createId } from '../utils'

/**
 * BudgetEngine — façade Command + Event + History.
 * Entry point for Editor, Template, AI, PDF, API and Mobile (future).
 */
export class BudgetEngine {
  private document: BudgetDocument | null = null
  private readonly bus: BudgetEventBus
  private readonly history: BudgetHistory
  private readonly calculator: BudgetCalculator
  private readonly validator: BudgetValidator
  private readonly serializer: BudgetSerializer
  private readonly now: () => Date
  private readonly idFactory: () => string

  constructor(options: BudgetEngineOptions = {}, deps?: {
    bus?: BudgetEventBus
    calculator?: BudgetCalculator
    validator?: BudgetValidator
    serializer?: BudgetSerializer
  }) {
    this.bus = deps?.bus ?? new BudgetEventBus()
    this.history = new BudgetHistory(options.historyLimit ?? 50)
    this.calculator = deps?.calculator ?? budgetCalculator
    this.validator = deps?.validator ?? budgetValidator
    this.serializer = deps?.serializer ?? budgetSerializer
    this.now = options.now ?? (() => new Date())
    this.idFactory = options.idFactory ?? createId
  }

  getDocument(): BudgetDocument | null {
    return this.document
  }

  /** Read-only snapshot clone for UI/API consumers. */
  peek(): BudgetDocument | null {
    return this.document ? this.serializer.toSnapshot(this.document).document : null
  }

  dispatch(command: BudgetCommand): BudgetCommandResult {
    const commandId = command.commandId ?? createCommandId()
    const enriched: BudgetCommand = { ...command, commandId, issuedAt: this.now().toISOString() }
    const ctx = {
      nowIso: this.now().toISOString(),
      idFactory: this.idFactory,
    }

    try {
      const skipHistory =
        enriched.type === 'CreateBudget' ||
        enriched.type === 'Validate' ||
        enriched.type === 'Recompute' ||
        enriched.type === 'RestoreSnapshot'

      if (this.document && !skipHistory) {
        this.history.push(this.document)
      }

      const applied = applyCommand(this.document, enriched, ctx)
      this.document = applied.document

      for (const event of applied.events) {
        this.bus.emit(event)
      }

      return {
        ok: true,
        document: applied.document,
        validation: applied.validation,
        events: applied.events,
      }
    } catch (error) {
      const code =
        error && typeof error === 'object' && 'code' in error
          ? String((error as { code: unknown }).code)
          : 'COMMAND_FAILED'
      const message = error instanceof Error ? error.message : 'Command failed'
      const failEvent: BudgetEvent = {
        id: this.idFactory(),
        type: 'budget.command_failed',
        budgetId: this.document?.id ?? 'unknown',
        commandId,
        timestamp: this.now().toISOString(),
        payload: { code, message, commandType: command.type },
      }
      this.bus.emit(failEvent)
      return {
        ok: false,
        document: this.document ?? appliedFallbackDocument(this.idFactory, this.now),
        events: [failEvent],
        error: { code, message },
      }
    }
  }

  validate(): BudgetValidationResult {
    if (!this.document) {
      return {
        valid: false,
        issues: [
          {
            severity: 'error',
            code: 'BUDGET_NOT_INITIALIZED',
            message: 'Nenhum orçamento carregado',
          },
        ],
      }
    }
    const result = this.validator.validate(this.document)
    this.dispatch({ type: 'Validate' })
    return result
  }

  undo(): BudgetCommandResult {
    if (!this.document || !this.history.canUndo()) {
      return {
        ok: false,
        document: this.document ?? appliedFallbackDocument(this.idFactory, this.now),
        events: [],
        error: { code: 'HISTORY_EMPTY', message: 'Nada para desfazer' },
      }
    }
    const previous = this.history.undo(this.document)
    if (!previous) {
      return {
        ok: false,
        document: this.document,
        events: [],
        error: { code: 'HISTORY_EMPTY', message: 'Nada para desfazer' },
      }
    }
    return this.dispatch({
      type: 'RestoreSnapshot',
      snapshot: this.serializer.toSnapshot(previous),
    })
  }

  redo(): BudgetCommandResult {
    if (!this.document || !this.history.canRedo()) {
      return {
        ok: false,
        document: this.document ?? appliedFallbackDocument(this.idFactory, this.now),
        events: [],
        error: { code: 'HISTORY_EMPTY', message: 'Nada para refazer' },
      }
    }
    const next = this.history.redo(this.document)
    if (!next) {
      return {
        ok: false,
        document: this.document,
        events: [],
        error: { code: 'HISTORY_EMPTY', message: 'Nada para refazer' },
      }
    }
    return this.dispatch({
      type: 'RestoreSnapshot',
      snapshot: this.serializer.toSnapshot(next),
    })
  }

  exportSnapshot(): BudgetSnapshot {
    if (!this.document) {
      throw new Error('BUDGET_NOT_INITIALIZED')
    }
    return this.serializer.toSnapshot(this.document)
  }

  exportJSON(): string {
    if (!this.document) {
      throw new Error('BUDGET_NOT_INITIALIZED')
    }
    return this.serializer.toJSON(this.document)
  }

  importJSON(raw: string): BudgetCommandResult {
    const document = this.serializer.fromJSON(raw)
    return this.dispatch({
      type: 'RestoreSnapshot',
      snapshot: this.serializer.toSnapshot(document),
    })
  }

  on(type: BudgetEventType | '*', listener: BudgetListener): () => void {
    return this.bus.on(type, listener)
  }

  historySize(): { undo: number; redo: number } {
    return this.history.size()
  }

  /** Expose calculator for pure math without mutating state. */
  getCalculator(): BudgetCalculator {
    return this.calculator
  }
}

function appliedFallbackDocument(idFactory: () => string, now: () => Date): BudgetDocument {
  const nowIso = now().toISOString()
  return {
    id: idFactory(),
    number: 'ORC-INVALID',
    title: '',
    status: 'DRAFT',
    client: { id: null, name: '' },
    lines: [],
    totals: {
      quantity: 0,
      costTotal: 0,
      subtotal: 0,
      discountTotal: 0,
      shipping: 0,
      taxes: 0,
      total: 0,
      profit: 0,
      marginPercent: 0,
    },
    globalDiscountPercent: 0,
    globalDiscountValue: 0,
    shipping: 0,
    taxRatePercent: 0,
    notes: '',
    version: 0,
    createdAt: nowIso,
    updatedAt: nowIso,
  }
}

export function createBudgetEngine(options?: BudgetEngineOptions): BudgetEngine {
  return new BudgetEngine(options)
}

/** Shared singleton for simple app wiring — prefer createBudgetEngine() in tests. */
export const budgetEngine = new BudgetEngine({}, { bus: budgetEventBus })
